import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import CloseIcon from "@mui/icons-material/Close";
import { Badge } from "@/components/common/Badge";
import { TooltipButton } from "@/components/common/TooltipButton";
import { navigatorButtonClasses } from "components/common/navigatorStyles";

/** An app page's column, pinned at a fixed height like a space's settings
 *  page: a form or a draft grows downward, so it gets none of a list page's
 *  fifth-of-the-pane drop. */
export const AppPageShell: React.FC<{
  maxWidth: string;
  /** Hold the page to the pane's height: the content scrolls inside, so a
   *  footer stays in view. */
  fill?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}> = ({ maxWidth, fill, ref, children }) => (
  <div
    ref={ref}
    className={`relative flex h-full w-full flex-col items-center px-6 lg:px-12 ${fill ? "overflow-hidden" : "overflow-y-auto"}`}
  >
    {/* With the column's 1.5rem, the first row sits level with the sidebar's Notifications row. */}
    <div className="shrink-0" style={{ height: "2.5rem" }} />
    {/* Below lg the column meets the pane's right edge, so a filled page clears the layout's version chip. */}
    <div
      className={`flex w-full flex-col gap-6 pb-10 ${fill ? "flex-1 min-h-0 lg:pb-6" : ""}`}
      style={{ maxWidth, paddingTop: "1.5rem" }}
    >
      {children}
    </div>
  </div>
);

export interface Crumb {
  label: string;
  to: string;
}

/** A page making an app, reached from the kind cards: its way back. */
export const NEW_APP_TRAIL: Crumb[] = [
  { label: "Applications", to: "/apps" },
  { label: "New app", to: "/apps/new" },
];

/** The path back out, then the page's title: a heading, or the field that
 *  names what the page makes. */
export const AppPageHead: React.FC<{
  trail: Crumb[];
  title: React.ReactNode;
  /** The app's kind, beside the title. */
  kind?: string;
  /** Shows an X at the top right that leaves the page. */
  onClose?: () => void;
  /** Beside the kind, on the title's row: the app's state. */
  aside?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ trail, title, kind, onClose, aside, children }) => {
  const navigate = useNavigate();
  return (
    <header className="flex flex-col gap-1.5">
      {(trail.length > 0 || onClose) && (
        <nav
          aria-label="Path"
          className="-ml-2 flex items-center gap-1 item-height"
        >
          {trail.map((c) => (
            <React.Fragment key={c.to}>
              <button
                type="button"
                onClick={() => navigate(c.to)}
                className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
              >
                {c.label}
              </button>
              <span className="text-fg-muted-regular">/</span>
            </React.Fragment>
          ))}
          {onClose && (
            <span className="ml-auto">
              <TooltipButton
                title="Close"
                aria-label="Close"
                variant="default"
                size="sm"
                onClick={onClose}
              >
                <CloseIcon />
              </TooltipButton>
            </span>
          )}
        </nav>
      )}
      <div className="flex items-center gap-3">
        {typeof title === "string" ? (
          <h1
            className="min-w-0 text-fg-title"
            style={{ margin: 0, textWrap: "balance" }}
          >
            {title}
          </h1>
        ) : (
          <div className="min-w-0">{title}</div>
        )}
        {kind && <Badge label={kind} size="xs" />}
        {aside}
      </div>
      {children}
    </header>
  );
};

/** The field a new or changing app is named in, set as the page's title. */
export function TitleInput({
  className = "",
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  ref?: React.Ref<HTMLInputElement>;
}) {
  return (
    <input
      aria-label="App name"
      maxLength={100}
      {...rest}
      // shortcut: Firefox has no `field-sizing` and keeps the input's default width; fine until it ships.
      className={`bg-transparent text-fg-title outline-none placeholder:text-fg-muted max-w-full [field-sizing:content] ${className}`}
      style={{ border: 0, padding: 0, caretColor: "var(--color-accent)" }}
    />
  );
}

/** An existing app's name, renamed where it stands: Enter or leaving saves,
 *  Escape or an empty name puts the saved one back. */
export function NameTitle({
  name,
  onRename,
}: {
  name: string;
  onRename: (next: string) => Promise<unknown>;
}) {
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  const commit = () => {
    const next = text.trim();
    if (!next || next === name) return setText(name);
    onRename(next).catch(() => setText(name));
  };
  return (
    <TitleInput
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setText(name);
      }}
      className="min-w-0"
    />
  );
}

/** The brief and what sends it: the text, then a bar for the model chip, a
 *  note and the button. */
export function Composer({
  invalid,
  grow,
  top,
  legend,
  bare,
  bar,
  ...field
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  ref?: React.Ref<HTMLTextAreaElement>;
  invalid?: boolean;
  /** Fit the text, up to a few lines, instead of holding `rows`. */
  grow?: boolean;
  /** What goes with the text, above it, as a message's attachments do. */
  top?: React.ReactNode;
  /** A title set in the border, as a fieldset's. */
  legend?: string;
  /** Inside a container that draws the edge: no border, fills its height. */
  bare?: boolean;
  bar: React.ReactNode;
}) {
  const Box = legend ? "fieldset" : "div";
  return (
    <Box
      className={`m-0 min-w-0 p-0 ${bare ? "flex-1" : "rounded-app border border-border bg-bg transition-colors focus-within:border-accent"}`}
      style={invalid ? { borderColor: "var(--color-error)" } : undefined}
    >
      {legend && (
        <legend className="ml-2 px-1.5 text-fg-muted-semibold">{legend}</legend>
      )}
      {/* A fieldset that is itself a flex box loses its border around the legend in some browsers. */}
      <div className={`h-full flex flex-col ${bare ? "p-2 gap-2" : ""}`}>
        {top && (
          <div
            className={`flex flex-wrap items-center gap-1.5 ${bare ? "" : "px-2 pt-2"}`}
          >
            {top}
          </div>
        )}
        <textarea
          rows={5}
          {...field}
          className="w-full flex-1 resize-none bg-transparent text-fg outline-none placeholder:text-fg-muted placeholder:italic"
          style={{
            border: 0,
            padding: bare
              ? 0
              : "var(--spacing-md) var(--spacing-lg) var(--spacing-sm)",
            lineHeight: 1.55,
            // shortcut: browsers without `field-sizing` keep `rows` and scroll; fine until Firefox ships it.
            ...(grow && { fieldSizing: "content", maxHeight: "6lh" }),
          }}
        />
        <div
          className={`flex flex-wrap items-center gap-2 ${bare ? "" : "px-2 pb-2"}`}
        >
          {bar}
        </div>
      </div>
    </Box>
  );
}
